import { formatTemporal, type Temporal } from "@/utils/temporal";
import store from "@/redux/store";
import React, { useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { BASE_API_URL_V2 } from "@/constants";
import fpFetch from "@/services/fpFetch";
import { openInteractionInViewer } from "../FpPages/RMS/Dashboard/Cards/openInViewer";

interface InteractionChoice {
  id: string;
  title: string;
  author: string[];
  occurrence: Temporal;
  notes_excerpt: string;
}
interface ChoicePage {
  items: InteractionChoice[];
  next_cursor: string | null;
}

/** A bare document has no selected row. Let the reader choose without changing its default. */
export const InteractionChoices = ({ blobId }: { blobId: string }) => {
  const [open, setOpen] = useState(false);
  const [selectionError, setSelectionError] = useState<string | null>(null);
  const { data, error, isFetching, hasNextPage, fetchNextPage } =
    useInfiniteQuery({
      queryKey: ["interactionChoices", blobId],
      initialPageParam: null as string | null,
      enabled: open,
      queryFn: async ({ pageParam, signal }): Promise<ChoicePage> => {
        const params = new URLSearchParams({ page_size: "50" });
        if (pageParam) params.set("cursor", pageParam);
        const response = await fpFetch(
          `${BASE_API_URL_V2}/api/v2/rms/interactions/by-blob/${encodeURIComponent(blobId)}/choices?${params}`,
          { signal }
        );
        if (!response.ok)
          throw new Error("Unable to load related interactions");
        const page: ChoicePage = await response.json();
        if (page.next_cursor && page.next_cursor === pageParam)
          throw new Error("Unable to load more interactions");
        return page;
      },
      getNextPageParam: (lastPage) => lastPage.next_cursor,
    });
  const choose = async (choice: InteractionChoice) => {
    setSelectionError(null);
    try {
      await openInteractionInViewer(choice.id, choice.title, undefined, {
        inlineViewerOnly: store.getState().fileViewerData.doNotOpenDrawer,
      });
    } catch {
      setSelectionError("Unable to open this interaction");
    }
  };
  return (
    <div className="border-b px-4 py-2 text-sm">
      <button
        type="button"
        aria-expanded={open}
        className="text-blue-700"
        onClick={() => setOpen(!open)}
      >
        Related interactions
      </button>
      {open && (
        <div className="space-y-2 py-2">
          {(error || selectionError) && (
            <p role="alert">{selectionError || error?.message}</p>
          )}
          {!error &&
            data?.pages
              .flatMap((page) => page.items)
              .map((choice) => (
                <button
                  type="button"
                  key={choice.id}
                  onClick={() => void choose(choice)}
                  className="block w-full rounded border p-2 text-left hover:bg-gray-50"
                >
                  <span className="block font-medium">{choice.title}</span>
                  <span className="block text-xs text-gray-600">
                    {choice.author.join(", ")}
                    {` · ${formatTemporal(choice.occurrence, "medium")}`}
                  </span>
                  <span className="block truncate text-xs">
                    {
                      new DOMParser().parseFromString(
                        choice.notes_excerpt,
                        "text/html"
                      ).body.textContent
                    }
                  </span>
                </button>
              ))}
          {isFetching && <p role="status">Loading interactions…</p>}
          {!isFetching &&
            data?.pages.every((page) => page.items.length === 0) && (
              <p>No related interactions.</p>
            )}
          {hasNextPage && (
            <button
              type="button"
              disabled={isFetching}
              onClick={() => void fetchNextPage()}
            >
              Load more interactions
            </button>
          )}
        </div>
      )}
    </div>
  );
};
